import { Component, OnInit, ChangeDetectorRef, ElementRef } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { CellEditingStartedEvent, ColDef, GridApi, ICellRendererParams, RowEditingStartedEvent, StartEditingCellParams } from 'ag-grid-enterprise';
import { GridService } from '../../../services/grid.service';
import { NgClass } from '@angular/common';
import { IconButtonComponent } from '../../../../popper/components/icon-button/icon-button.component';
import { IconButtonColorEnumPipe } from '../../../../popper/components/icon-button/icon-button-color-enum.pipe';
import { IEditHistoryCellRendererParams } from '../../../models/edit-history-cell-renderer-params.model';

@Component({
    selector: 'edit-history-cell',
    templateUrl: './edit-history-cell.component.html',
    styleUrls: ['./edit-history-cell.component.scss'],
    standalone: true,
    imports: [IconButtonComponent, NgClass, IconButtonColorEnumPipe]
})
export class EditHistoryCellComponent implements ICellRendererAngularComp {

  private static lastTabShift = false;
  private static tabTrackerActive = false;
  private cellName: string = '';
  public params: IEditHistoryCellRendererParams;
  public data: any;
  public originalData: any;
  public api: GridApi;
  public rowIndex: number = -1;
  public readOnly: boolean = true;
  public isPending: boolean = false;
  public isDisabled: boolean = false;
  public hideDelete: boolean = false;
  public modalEdit: boolean = false;
  public showModalEditButton: boolean = false;
  public editTitle: string = "Edit";
  public historyTitle: string = "History";
  public deleteTitle: string = "Delete";
  public cancelTitle: string = "Cancel";
  public saveTitle: string = "Save";

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    protected gridService: GridService,
    private elementRef: ElementRef
  ) {
    this.readOnly = gridService.readOnly.getValue();
  }

  public agInit(params: IEditHistoryCellRendererParams): void {
    this.params = params;
    this.applyRowState(params);
    this.api.addEventListener(
      'cellEditingStarted',
      this.onCellEditingStarted.bind(this)
    );
    this.api.addEventListener("rowEditingStarted", this.rowEditingStartedEventHandler.bind(this));
    this.cellName = params?.colDef?.cellRenderer?.toString() || '';
    this.originalData = JSON.parse(JSON.stringify(this.data));
    if (params.context && params.context.userAdmin) {
      this.hideDelete = true;
    }
    if (params.context && params.context.modalEdit) {
      this.modalEdit = true;
      this.readOnly = false;
    }
    this.showModalEditButton = this.modalEdit && (this.data.status === "Active" || this.data.status === "Pending Approval");
    if (params.context && params.context.parent) {
      const parent = params.context.parent;
      switch (parent) {
        case "manage-org-users":
          this.editTitle = "Edit user's account";
          this.historyTitle = "View user's account history with this organization";
          this.deleteTitle = "Delete user's pending account";
          this.cancelTitle = "Cancel user's account edit without saving changes";
          this.saveTitle = "Save user's account edits";
          break;
        case "manage-org-relations":
          this.editTitle = "Edit relationship";
          this.historyTitle = "View expanded history";
          this.cancelTitle = "Cancel edit without saving changes";
          this.saveTitle = "Save edits";
          break;

        default:
          break;
      }
    }

    if (!EditHistoryCellComponent.tabTrackerActive) {
      document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Tab') EditHistoryCellComponent.lastTabShift = e.shiftKey;
      }, true);
      EditHistoryCellComponent.tabTrackerActive = true;
    }

    const agCell = ((params as any).eParentOfValue as HTMLElement)?.closest('.ag-cell') as HTMLElement;
    if (agCell) {
      agCell.addEventListener('focus', () => this.focusIn());
    }
  }

  onCellEditingStarted(event: CellEditingStartedEvent): void {
    event.node.id;
  }

  rowEditingStartedEventHandler(event: RowEditingStartedEvent): void {
    event.node.id;
  }

  public focusIn(): void {
    const container = this.elementRef.nativeElement.querySelector('.button-container') as HTMLElement;
    if (!container) return;
    const buttons = Array.from(container.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
    if (buttons.length === 0) return;
    const target = EditHistoryCellComponent.lastTabShift ? buttons[buttons.length - 1] : buttons[0];
    target.focus();
  }

  public onButtonTabKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    const container = event.currentTarget as HTMLElement;
    const buttons = Array.from(container.querySelectorAll<HTMLButtonElement>('button:not([disabled])'));
    if (buttons.length <= 1) return;
    const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (currentIndex === -1) return;
    if (!event.shiftKey && currentIndex === buttons.length - 1) return;
    if (event.shiftKey && currentIndex === 0) return;
    event.preventDefault();
    event.stopPropagation();
    buttons[event.shiftKey ? currentIndex - 1 : currentIndex + 1].focus();
  }

  public refresh(params: ICellRendererParams): boolean {
    //Return true so ag-grid keeps this renderer instead of destroying and recreating it. When Tab
    //moves focus into this cell, ag-grid refreshes the row immediately afterwards, and a recreate
    //would take the just-focused button out of the DOM, dropping focus to the body and costing an
    //extra Tab to reach the button. Only the row's own state is re-derived here - the originalData
    //snapshot and the context driven flags are set once in agInit and must survive a refresh.
    this.applyRowState(params);
    return true;
  }

  private applyRowState(params: ICellRendererParams): void {
    this.data = params.data;
    this.api = params.api;
    this.rowIndex = params.node.rowIndex;
    this.isPending = (this.data.status && this.data.status === "Pending");
    this.isDisabled = this.data.cannotEdit === true;
    this.showModalEditButton = this.modalEdit && (this.data.status === "Active" || this.data.status === "Pending Approval");
  }

  public toggleReadOnly(): void {
    this.readOnly = !this.readOnly;
    console.log('start of togglereadonly', this.readOnly);
    const gridData = this.getFilteredAndSorted();
    if (gridData.length > 0) {
      this.rowIndex = gridData.indexOf(this.data);
    }
    this.gridService.selectedTable.next(this.cellName);
    this.gridService.readOnly.next(this.readOnly);
    this.gridService.rowIdex.next(this.rowIndex);
    //this.data.disabled = this.readOnly = !this.readOnly;
    this.data.disabled = this.readOnly;
    //this.gridService.selectedTable.next(this.cellName);
    //this.gridService.readOnly.next(this.readOnly);

    console.log('before actions togglereadonly', this.readOnly);
    if (!this.readOnly) {
      let colDefs = this.params.api.getColumnDefs() as Array<ColDef>;
      let nodeRows = this.api.getCellRendererInstances({ rowNodes: [this.params.node] });
      //console.log('startediting togglereadonly', this.readOnly);
      this.api.startEditingCell({
        rowIndex: this.rowIndex,
        colKey: this.params.columnToStartEditKey,
        rowPinned: this.params.node.rowPinned
      } as StartEditingCellParams);
      let cells = this.api.getEditingCells();
    } else {
      //console.log('stopediting togglereadonly', this.readOnly);
      this.api.stopEditing(true);
      this.api.redrawRows();
    }

    /*
    if (this.api.onActionTrigger) {
      console.log('at togglereadonly', this.readOnly);
      this.api.onActionTrigger({ row: this.rowIndex, action: "toggleReadOnly", data: this.data, cellName: this.cellName, ro: this.readOnly });
      this.api.redrawRows();
    }
    */
    //this.changeDetectorRef.detectChanges();
    console.log('end of togglereadonly', this.readOnly);
  }

  public deleteRow(): void {
    if ((this.api as any).onActionTrigger) {
      (this.api as any).onActionTrigger({ row: this.rowIndex, action: "delete", data: this.data });
    } else {
      console.error("OnActionTrigger not defined");
    }
  }

  public showHistory(): void {
    if ((this.api as any).onActionTrigger) {
      (this.api as any).onActionTrigger({ row: this.rowIndex, action: "showHistory", data: this.data });
    } else {
      console.error("OnActionTrigger not defined");
    }
  }

  public triggerEdit(): void {
    if ((this.api as any).onActionTrigger) {
      (this.api as any).onActionTrigger({ row: this.rowIndex, action: "triggerEdit", data: this.data });
    } else {
      console.error("OnActionTrigger not defined");
    }
  }

  public save(): void {
    this.data.disabled = this.readOnly = true;
    this.gridService.readOnly.next(this.readOnly);
    this.api.redrawRows();
    this.api.stopEditing();

    if ((this.api as any).onActionTrigger) {
      (this.api as any).onActionTrigger({ row: this.rowIndex, action: "editComplete", data: this.data, originalData: this.originalData });
    }
  }

  getFilteredAndSorted(): any[] {
    let data: any[] = [];
    this.api.forEachNodeAfterFilterAndSort(function(node: { data: any; }) {
      data.push(node.data);
    });
    return data;
  }

}
